import { create } from 'zustand';
import { User, Preferences } from '../../../core/types';
import { storageMgt } from '../../../core/utils/storage';
import * as api from '../../../core/api/services';
import { RegisterModel, VerifyOtpModel } from '../../../core/types';

interface AuthState {
  user: User | null;
  preferences: Preferences | null;
  isAuthenticated: boolean;
  lastVerifyError: string;

  setUser: (user: User | null) => void;
  setPreferences: (prefs: Preferences | null) => void;
  cacheUser: (user: User) => void;
  retrieveUser: () => User | null;
  logout: () => void;

  therappLogin: (phone: string) => Promise<User>;
  register: (data: RegisterModel) => Promise<User>;
  verifyLogin: (data: VerifyOtpModel) => Promise<boolean>;
  sendOtp: (phone: string) => Promise<string>;
  saveUser: (user: User) => Promise<string>;
  savePrefs: (prefs: Preferences) => Promise<Preferences>;
  editPrefs: (prefs: Preferences) => Promise<Preferences>;
  fetchPrefs: (userId: string) => Promise<Preferences | null>;  // ← null when not found
  checkPrefs: (userId: string) => Promise<boolean>;
  saveLocalPrefs: (prefs: Preferences) => void;
  retrieveLocalPrefs: () => Preferences | null;
}

const initialUser: User = { firstName: '', lastName: '' };

export const useAuthStore = create<AuthState>((set, get) => ({
  user: storageMgt.getUser(),
  preferences: storageMgt.getPrefs(),
  isAuthenticated: storageMgt.isAuthenticated(),
  lastVerifyError: '',

  setUser: (user) => set({ user }),
  setPreferences: (prefs) => set({ preferences: prefs }),

  cacheUser: (user: User) => {
    storageMgt.saveUser(user);
    storageMgt.setAuthenticated(true);
    set({ user, isAuthenticated: true });
  },

  retrieveUser: () => {
    return get().user || storageMgt.getUser();
  },

  logout: () => {
    storageMgt.logout();
    set({ user: null, preferences: null, isAuthenticated: false });
  },

  therappLogin: async (phone: string) => {
    try {
      return await api.therappLogin(phone);
    } catch {
      return { ...initialUser, message: 'No user found... Please register!' };
    }
  },

  register: async (data: RegisterModel) => {
    try {
      return await api.registerUser(data);
    } catch (err: any) {
      return { ...initialUser, message: err?.response?.data?.message || 'Registration failed.' };
    }
  },

  verifyLogin: async (data: VerifyOtpModel) => {
    try {
      const result = await api.verifyOtp(data);
      set({ lastVerifyError: result ? '' : 'Invalid or expired code. Please try again.' });
      return result;
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === 'string' ? err.response.data : null) ||
        'Verification failed.';
      set({ lastVerifyError: msg });
      return false;
    }
  },

  sendOtp: async (phone: string) => {
    try {
      return await api.sendOtp(phone);
    } catch {
      return '';
    }
  },

  saveUser: async (user: User) => {
    try {
      return await api.saveUserInfo(user);
    } catch (err: any) {
      return err?.response?.data?.message || 'Failed to save user.';
    }
  },

  savePrefs: async (prefs: Preferences) => {
    try {
      const result = await api.savePreferences(prefs);
      storageMgt.savePrefs(result);
      set({ preferences: result });
      return result;
    } catch {
      return {};
    }
  },

  editPrefs: async (prefs: Preferences) => {
    try {
      // Use id from prefs itself, fall back to stored prefs
      const id = prefs.id || storageMgt.getPrefs()?.id;
      if (!id) {
        // No existing id — create instead
        return get().savePrefs(prefs);
      }
      const result = await api.editPreferences({ ...prefs, id });
      storageMgt.savePrefs(result);
      set({ preferences: result });
      return result;
    } catch {
      return {};
    }
  },

  fetchPrefs: async (userId: string) => {
    try {
      const result = await api.fetchPreferences(userId);
      if (result?.id) {
        storageMgt.savePrefs(result);
        set({ preferences: result });
        return result;
      }
      return null;   // ← no prefs found for this user
    } catch {
      return null;   // ← network/server error — treat as no prefs
    }
  },

  checkPrefs: async (userId: string) => {
    try {
      return await api.checkPreferences(userId);
    } catch {
      return false;
    }
  },

  saveLocalPrefs: (prefs: Preferences) => {
    storageMgt.savePrefs(prefs);
    set({ preferences: prefs });
  },

  retrieveLocalPrefs: () => {
    return storageMgt.getPrefs();
  },
}));